import { Link, useParams } from "react-router-dom";
import { Typography, Stack, ButtonBase, Menu, MenuItem as MuiMenuItem } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useState } from "react";

import logUserAction from "../../apis/userLogger";
import { USER_ROLES } from "../../utils/constants";
import { isModuleEnabled } from "../../utils/moduleHelpers";

const PatientMenu = ({ active, data, trialFilter }) => {
  const { _id_ } = useParams();

  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data?.role;
  const trial_name =
    data?.participant_id?.substring(data?.participant_id.indexOf("["))?.trim() || "No ID";

  const hasRole = (...roles) => roles.includes(role);

  const withTrial = (path) => (trialFilter ? `${path}?trial=${trialFilter}` : path);

  const handleLog = (label) => () => {
    logUserAction(`Click on ${label}`, data?.email, trial_name);
  };

  const MenuItem = ({ label, to, onClick, activeKey }) => (
    <ButtonBase
      component={Link}
      to={to}
      onClick={onClick}
      data-testid={`patient-tab-${activeKey}`}
      sx={{
        px: 2,
        py: 1.5,
        backgroundColor: active === activeKey ? "#EE295D" : "white",
        color: active === activeKey ? "white" : "#EE295D",
        "&:hover": {
          backgroundColor: "#EE295D",
          color: "white",
        },
        display: "flex",
        alignItems: "center",
        flex: "0 0 165px",
        maxWidth: "165px",
        flexShrink: 0,
        textAlign: "center",
      }}
    >
      <>
        <Typography fontSize="0.85rem" fontWeight={"fontWeightBold"}>
          {label}
        </Typography>
      </>
    </ButtonBase>
  );

  const LogsDropdown = ({ label = "Logs", items }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    return (
      <>
        <ButtonBase
          onClick={handleOpen}
          sx={{
            px: 2,
            py: 1.5,
            backgroundColor: items.some((i) => i.activeKey === active) ? "#EE295D" : "white",
            color: items.some((i) => i.activeKey === active) ? "white" : "#EE295D",
            "&:hover": {
              backgroundColor: "#EE295D",
              color: "white",
            },
            flex: "0 0 165px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
          }}
        >
          <Typography fontSize="0.85rem" fontWeight={"fontWeightBold"}>
            {label}
          </Typography>

          <ArrowDropDownIcon
            sx={{
              fontSize: "1rem",
              transition: "transform 0.2s ease",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </ButtonBase>

        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          {items.map((item) => (
            <MuiMenuItem
              key={item.label}
              component={Link}
              to={item.to}
              onClick={() => {
                item.onClick?.();
                handleClose();
              }}
              sx={{ fontSize: "0.85rem", fontWeight: "fontWeightBold" }}
            >
              {item.label}
            </MuiMenuItem>
          ))}
        </Menu>
      </>
    );
  };

  const logItems = [];

  // External photo logs
  if (
    hasRole(
      USER_ROLES.INVESTIGATOR,
      USER_ROLES.SUB_INVESTIGATOR,
      USER_ROLES.CRO,
      USER_ROLES.CRO_COORDINATOR,
      USER_ROLES.DATA_MANAGER,
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.CRC
    ) &&
    data?.show_external_photos
  ) {
    logItems.push({
      label: "External Photo Logs",
      to: withTrial(`/patient/${_id_}/external_photo_logs`),
      activeKey: "externalPhotoLogs",
      onClick: handleLog("External Photo Logs Tab"),
    });
  }

  // External video logs
  if (
    hasRole(
      USER_ROLES.INVESTIGATOR,
      USER_ROLES.SUB_INVESTIGATOR,
      USER_ROLES.CRO,
      USER_ROLES.CRO_COORDINATOR,
      USER_ROLES.DATA_MANAGER,
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.CRC
    ) &&
    data?.show_external_videos
  ) {
    logItems.push({
      label: "External Video Logs",
      to: withTrial(`/patient/${_id_}/external_video_logs`),
      activeKey: "externalVideoLogs",
      onClick: handleLog("External Video Logs Tab"),
    });
  }

  // eCRF change logs
  if (data?.has_trial && !hasRole(USER_ROLES.REVIEWER) && !data?.is_session_based) {
    logItems.push({
      label: "eCRF Change Logs",
      to: withTrial(`/patient/${_id_}/ecrf_change_logs`),
      activeKey: "changeLogs",
      onClick: handleLog("eCRF Change Logs Tab"),
    });
  }

  // Visit config logs
  if (data?.has_trial && !hasRole(USER_ROLES.REVIEWER) && !data?.is_session_based) {
    logItems.push({
      label: "Visit Config Logs",
      to: withTrial(`/patient/${_id_}/visit_config_logs`),
      activeKey: "visitConfigLogs",
      onClick: handleLog("Visit Config Logs Tab"),
    });
  }

  // Query change logs
  if (
    hasRole(
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.CRO,
      USER_ROLES.CRO_COORDINATOR,
      USER_ROLES.INVESTIGATOR,
      USER_ROLES.CRC,
      USER_ROLES.SUB_INVESTIGATOR,
      USER_ROLES.DATA_MANAGER
    ) &&
    data?.has_trial &&
    !data?.is_session_based
  ) {
    logItems.push({
      label: "Query Change Logs",
      to: withTrial(`/patient/${_id_}/query_change_logs`),
      activeKey: "QueryChangeLogs",
      onClick: handleLog("Query Change Logs Tab"),
    });
  }

  // Auto notification logs
  if (!hasRole(USER_ROLES.REVIEWER, USER_ROLES.CLINIC_ADMIN)) {
    logItems.push({
      label: "Auto Notifications Log",
      to: withTrial(`/patient/${_id_}/notification_list`),
      activeKey: "notifications",
      onClick: handleLog("Auto Notifications Log Tab"),
    });
  }

  return (
    <>
      {/* Menu Items */}
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={1}
        backgroundColor="#f1f1f1"
        sx={{
          p: 2,
          pt: 1,
        }}
      >
        {/* Basic Information Section */}
        {!hasRole(
          USER_ROLES.REVIEWER,
          USER_ROLES.CRO,
          USER_ROLES.CRO_COORDINATOR,
          USER_ROLES.DATA_MANAGER
        ) && (
          <>
            {!hasRole(USER_ROLES.INVESTIGATOR, USER_ROLES.CRC, USER_ROLES.SUB_INVESTIGATOR) && (
              <MenuItem
                label="Personal Info"
                to={withTrial(`/patient/${_id_}/info`)}
                onClick={handleLog("Personal Info")}
                activeKey="info"
              />
            )}
            <MenuItem
              label="Appointments"
              to={withTrial(`/patient/${_id_}/appointment`)}
              onClick={handleLog("Appointments Tab")}
              activeKey="appointments"
            />
            <MenuItem
              label="Messages"
              to={withTrial(`/patient/${_id_}/messages`)}
              onClick={handleLog("Messages Tab")}
              activeKey="messages"
            />
          </>
        )}

        {isModuleEnabled(data?.enabled_modules, "Photos") && (
          <MenuItem
            label="APP Photos"
            to={withTrial(`/patient/${_id_}/photos`)}
            onClick={handleLog("App Photos Tab")}
            activeKey="photos"
          />
        )}
        {isModuleEnabled(data?.enabled_modules, "Videos") && (
          <MenuItem
            label="APP Videos"
            to={withTrial(`/patient/${_id_}/videos`)}
            onClick={handleLog("App Videos Tab")}
            activeKey="videos"
          />
        )}
        <MenuItem
          label="APP Documents"
          to={withTrial(`/patient/${_id_}/app-documents`)}
          onClick={handleLog("App Documents Tab")}
          activeKey="app-documents"
        />

        {hasRole(
          USER_ROLES.INVESTIGATOR,
          USER_ROLES.SUB_INVESTIGATOR,
          USER_ROLES.CRO,
          USER_ROLES.CRO_COORDINATOR,
          USER_ROLES.DATA_MANAGER,
          USER_ROLES.SUPER_ADMIN,
          USER_ROLES.CRC,
          USER_ROLES.REVIEWER
        ) &&
          data?.show_external_photos && (
            <MenuItem
              label="External Photos"
              to={withTrial(`/patient/${_id_}/sessions_external_photos`)}
              onClick={handleLog("External Photos Tab")}
              activeKey="externalPhotos"
            />
          )}

        {hasRole(
          USER_ROLES.INVESTIGATOR,
          USER_ROLES.SUB_INVESTIGATOR,
          USER_ROLES.CRO,
          USER_ROLES.CRO_COORDINATOR,
          USER_ROLES.DATA_MANAGER,
          USER_ROLES.SUPER_ADMIN,
          USER_ROLES.CRC,
          USER_ROLES.REVIEWER
        ) &&
          data?.show_external_videos && (
            <MenuItem
              label="External Videos"
              to={withTrial(`/patient/${_id_}/sessions_external_videos`)}
              onClick={handleLog("External Videos Tab")}
              activeKey="externalVideos"
            />
          )}

        {hasRole(
          USER_ROLES.CLINIC_OWNER,
          USER_ROLES.CLINIC_ADMIN,
          USER_ROLES.SUPER_ADMIN,
          USER_ROLES.INVESTIGATOR,
          USER_ROLES.SUB_INVESTIGATOR,
          USER_ROLES.CRC,
          USER_ROLES.CRO,
          USER_ROLES.CRO_COORDINATOR,
          USER_ROLES.DATA_MANAGER
        ) &&
          data?.show_external_document && (
            <MenuItem
              label="External Documents"
              to={withTrial(`/patient/${_id_}/documents`)}
              onClick={handleLog("External Documents Tab")}
              activeKey="documents"
            />
          )}

        {!hasRole(USER_ROLES.CLINIC_ADMIN) && (
          <>
            {data?.has_trial && !data?.is_session_based && (
              <MenuItem
                label="eCRF"
                to={withTrial(`/patient/${_id_}/ecrf`)}
                onClick={handleLog("eCRF Tab")}
                activeKey="ecrf"
              />
            )}

            {hasRole(
              USER_ROLES.SUPER_ADMIN,
              USER_ROLES.CRO,
              USER_ROLES.CRO_COORDINATOR,
              USER_ROLES.INVESTIGATOR,
              USER_ROLES.CRC,
              USER_ROLES.SUB_INVESTIGATOR,
              USER_ROLES.DATA_MANAGER
            ) &&
              data?.has_trial &&
              !data?.is_session_based && (
                <MenuItem
                  label="Query/eSignature"
                  to={withTrial(`/patient/${_id_}/query`)}
                  onClick={handleLog("Query/eSignature Tab")}
                  activeKey="query"
                />
              )}

            {!hasRole(USER_ROLES.REVIEWER) && (
              <>
                {isModuleEnabled(data?.enabled_modules, "POEM") && (
                  <MenuItem
                    label="POEMs/eDiary"
                    to={withTrial(`/patient/${_id_}/poem`)}
                    onClick={handleLog("POEMs/eDiary Tab")}
                    activeKey="poem"
                  />
                )}
                {isModuleEnabled(data?.enabled_modules, "Advanced Surveys") && (
                  <MenuItem
                    label="ePRO"
                    to={withTrial(`/patient/${_id_}/advanced-surveys`)}
                    onClick={handleLog("ePRO")}
                    activeKey="advanced-surveys"
                  />
                )}
                {data?.has_ediary && (
                  <MenuItem
                    label={data?.ediary_name || "eDiary"}
                    to={withTrial(`/patient/${_id_}/ediary`)}
                    onClick={handleLog("eDiary Tab")}
                    activeKey="ediary"
                  />
                )}
                <MenuItem
                  label="eConsent"
                  to={withTrial(`/patient/${_id_}/e_consent`)}
                  onClick={handleLog("eConsent Tab")}
                  activeKey="econsent"
                />
              </>
            )}
          </>
        )}

        {/* Activities Section */}
        {!hasRole(
          USER_ROLES.REVIEWER,
          USER_ROLES.CRO,
          USER_ROLES.CRO_COORDINATOR,
          USER_ROLES.DATA_MANAGER
        ) && (
          <>
            {hasRole(USER_ROLES.SUPER_ADMIN) && (
              <MenuItem
                label="Activity"
                to={`/patient/${_id_}/activity`}
                onClick={handleLog("eActivity Tab")}
                activeKey="activity"
              />
            )}

            {data?.show_survey_histogram && (
              <MenuItem
                label="Histogram"
                to={withTrial(`/patient/${_id_}/histograms`)}
                onClick={handleLog("Histogram Tab")}
                activeKey="histogram"
              />
            )}
          </>
        )}

        {hasRole(
          USER_ROLES.SUPER_ADMIN,
          USER_ROLES.INVESTIGATOR,
          USER_ROLES.SUB_INVESTIGATOR,
          USER_ROLES.CRC,
          USER_ROLES.REVIEWER
        ) &&
          data?.is_rewards && (
            <MenuItem
              label="Rewards"
              to={withTrial(`/patient/${_id_}/reward`)}
              onClick={handleLog("Reward Tab")}
              activeKey="reward"
            />
          )}

        {/* Visit Status */}
        {hasRole(
          USER_ROLES.INVESTIGATOR,
          USER_ROLES.SUB_INVESTIGATOR,
          USER_ROLES.CRC,
          USER_ROLES.CRO,
          USER_ROLES.CRO_COORDINATOR,
          USER_ROLES.SUPER_ADMIN,
          USER_ROLES.DATA_MANAGER
        ) &&
          data?.has_trial && (
            <MenuItem
              label="Visit Status"
              to={withTrial(`/patient/${_id_}/visit_status`)}
              onClick={handleLog("Visit Status Tab")}
              activeKey="status"
            />
          )}

        {logItems.length > 0 && <LogsDropdown label="Logs" items={logItems} />}
      </Stack>
    </>
  );
};

export default PatientMenu;
